import { CountryCode, ValidationError } from "packable-common";
import { BoxDimension, BoxSide, BoxWeight, DimensionName } from "./Validators.types";

const COUNTRY_CONFIG = {
  US: {
    labels: {
      dimensionUnit: "inches",
      weightUnit: "pounds",
    },
    validators: {
      dimension: {
        inputValidator: /^[0-9]{1,3}$/,
        A: {
          min: 6,
          max: 108,
        },
        B: {
          min: 3,
          max: 61,
        },
        C: {
          min: 1,
          max: 59,
        },
        G: {
          min: 8,
          max: 124,
        },
        T: {
          min: 14,
          max: 130,
        },
      },
      weight: {
        inputValidator: /^[0-9]{1,2}$/,
        min: 1,
        max: 70,
      },
    },
  },

  CA: {
    labels: {
      dimensionUnit: "inches",
      weightUnit: "pounds",
    },
    validators: {
      dimension: {
        inputValidator: /^[0-9]{1,3}$/,
        A: {
          min: 6,
          max: 108,
        },
        B: {
          min: 3,
          max: 61,
        },
        C: {
          min: 1,
          max: 59,
        },
        G: {
          min: 8,
          max: 124,
        },
        T: {
          min: 14,
          max: 130,
        },
      },
      weight: {
        inputValidator: /^[0-9]{1,2}$/,
        min: 1,
        max: 70,
      },
    },
  },

  GB: {
    labels: {
      dimensionUnit: "centimeters",
      weightUnit: "kilograms",
    },
    validators: {
      dimension: {
        inputValidator: /^[0-9]{1,3}$/,
        A: {
          min: 1,
          max: 150,
        },
        B: {
          min: 1,
          max: 148,
        },
        C: {
          min: 1,
          max: 148,
        },
        G: {
          min: 4,
          max: 299,
        },
        T: {
          min: 5,
          max: 300,
        },
      },
      weight: {
        inputValidator: /^[0-9]{1,2}(\.[0-9]{0,2})?$/,
        min: 0.1,
        max: 30,
      },
    },
  },
} as const;

function validateDimensionMin(
  countryCode: CountryCode,
  boxSide: keyof typeof BoxSide,
  value: number | null | undefined
): boolean {
  if (!value) return false;
  const validationValue =
    COUNTRY_CONFIG[countryCode].validators.dimension[boxSide].min;
  return value >= validationValue;
}

function validateDimensionMax(
  countryCode: CountryCode,
  boxSide: keyof typeof BoxSide,
  value: number | null | undefined
): boolean {
  if (!value) return false;
  const validationValue =
    COUNTRY_CONFIG[countryCode].validators.dimension[boxSide].max;
  return value <= validationValue;
}

function validateDimensionInput(
  countryCode: CountryCode,
  value: string | null | undefined
): boolean {
  if (!value) return false;
  const validator =
    COUNTRY_CONFIG[countryCode].validators.dimension.inputValidator;
  const result = validator.test(value);
  return result;
}

function validateWeightMin(
  countryCode: CountryCode,
  value: number | null | undefined
): boolean {
  if (!value) return false;
  const validationValue =
    COUNTRY_CONFIG[countryCode].validators.weight.min;
  return value >= validationValue;
}

function validateWeightMax(
  countryCode: CountryCode,
  value: number | null | undefined
): boolean {
  if (!value) return false;
  const validationValue =
    COUNTRY_CONFIG[countryCode].validators.weight.max;
  return value <= validationValue;
}

function validateWeightInput(
  countryCode: CountryCode,
  value: string | null | undefined
): boolean {
  if (!value) return false;
  const validator =
    COUNTRY_CONFIG[countryCode].validators.weight.inputValidator;
  return validator.test(value);
}
export function validateBoxDimensions(
  countryCode: CountryCode,
  itemLevelName: string,
  length: BoxDimension,
  width: BoxDimension,
  height: BoxDimension,
  setValidationError: (validationError: ValidationError) => void,
  removeValidationError: (fieldName: string) => void
): void {
  const [sideA, sideB, sideC]: Array<BoxDimension> = [
    length,
    width,
    height,
  ].sort((a, b) => Number(b.value) - Number(a.value));
  const sideT: BoxDimension = {
    name: DimensionName.TOTAL,
    fieldName: itemLevelName,
    value: Number(sideA.value) + (Number(sideB.value) * 2 + Number(sideC.value) * 2),
  };
    // validate side A (largest item dimension)
  const validationErrorA = validateBoxDimension(countryCode, BoxSide.A, sideA);
  if (validationErrorA !== null) {
    setValidationError(validationErrorA);
  }
  else {
    removeValidationError(sideA.fieldName);
  }
  // validate side B (medium item dimension)
  const validationErrorB = validateBoxDimension(countryCode, BoxSide.B, sideB);
  if (validationErrorB !== null) {
    setValidationError(validationErrorB);
  }
  else {
    removeValidationError(sideB.fieldName);
  }
  // validate side C (smallest item dimension)
  const validationErrorC = validateBoxDimension(countryCode, BoxSide.C, sideC);
  if (validationErrorC !== null) {
    setValidationError(validationErrorC);
  }
  else {
    removeValidationError(sideC.fieldName);
  }
  // validate total dimension
  const validationErrorT = validateBoxDimension(countryCode, BoxSide.T, sideT);
  if (validationErrorT !== null) {
    setValidationError(validationErrorT);
  }
  else {
    removeValidationError(sideT.fieldName);
  }
}

export function validateBoxDimension(
  countryCode: CountryCode,
  boxSide: BoxSide,
  dimension: BoxDimension
): ValidationError | null {
  if (dimension.value === undefined || dimension.value === null || dimension.value.toString().length === 0) {
    return {
      fieldName: dimension.fieldName,
      errorMessage: `Box ${dimension.name} can not be blank`,
    };
  }
  if (! validateDimensionInput(countryCode, dimension.value.toString())) {
    return {
      fieldName: dimension.fieldName,
      errorMessage: `Box ${dimension.name} value should contain only digits`,
    };
  }
  if (! validateDimensionMin(countryCode, boxSide, dimension.value)) {
    return {
      fieldName: dimension.fieldName,
      errorMessage: `Box ${dimension.name} value should be no less than ${COUNTRY_CONFIG[countryCode].validators.dimension[boxSide].min} ${COUNTRY_CONFIG[countryCode].labels.dimensionUnit}`,
    };
  }
  if (! validateDimensionMax(countryCode, boxSide, dimension.value)) {
    return {
      fieldName: dimension.fieldName,
      errorMessage: `Box ${dimension.name} value should be no more than ${COUNTRY_CONFIG[countryCode].validators.dimension[boxSide].max} ${COUNTRY_CONFIG[countryCode].labels.dimensionUnit}. Current value is ${dimension.value}`,
    };
  }
  return null;
}

export function validateBoxWeight(
  countryCode: CountryCode,
  weight: BoxWeight,
  setValidationError: (validationError: ValidationError) => void,
  removeValidationError: (fieldName: string) => void
): void {
  if (weight.value === undefined || weight.value === null || weight.value.toString().length === 0) {
    setValidationError({
      fieldName: weight.fieldName,
      errorMessage: `Box ${weight.name} can not be blank`,
    });
    return;
  }
  if (! validateWeightInput(countryCode, weight.value.toString())) {
    setValidationError({
      fieldName: weight.fieldName,
      errorMessage: `Box ${weight.name} value should contain only digits with up to 2 digits after decimal point`,
    });
    return;
  }
  if (! validateWeightMin(countryCode, weight.value)) {
    setValidationError({
      fieldName: weight.fieldName,
      errorMessage: `Box ${weight.name} value should be no less than ${COUNTRY_CONFIG[countryCode].validators.weight.min} ${COUNTRY_CONFIG[countryCode].labels.weightUnit}`,
    });
    return;
  }
  if (! validateWeightMax(countryCode, weight.value)) {
    setValidationError({
      fieldName: weight.fieldName,
      errorMessage: `Box ${weight.name} value should be no more than ${COUNTRY_CONFIG[countryCode].validators.weight.max} ${COUNTRY_CONFIG[countryCode].labels.weightUnit}`,
    });
    return;
  }
  removeValidationError(weight.fieldName);
}
